import React, { useState, useEffect, useCallback } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  Clock, HelpCircle, BarChart3, Shield, RefreshCw, Play, ArrowLeft,
  CheckCircle, XCircle,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { ConfirmModal } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./StudentAssessments.scss";

const MODE_LABELS = { practice: "Practice", exam: "Exam", sectioned_exam: "Sectioned Exam" };

const AttemptsTable = ({ attempts, navigate }) => {
  if (!attempts.length) return null;
  return (
    <div className="attempts-history">
      <div className="attempts-history__title">Previous Attempts</div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Score</th>
            <th>Percentage</th>
            <th>Status</th>
            <th>Date</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {attempts.map((a) => (
            <tr key={a._id}>
              <td>{a.attemptNumber}</td>
              <td>{a.score}/{a.totalMarks}</td>
              <td>{a.percentage}%</td>
              <td>
                {a.passed ? (
                  <span className="d-inline-flex align-items-center gap-1 text-success"><CheckCircle size={12} /> Passed</span>
                ) : (
                  <span className="d-inline-flex align-items-center gap-1 text-danger"><XCircle size={12} /> Failed</span>
                )}
              </td>
              <td>{a.submittedAt ? new Date(a.submittedAt).toLocaleDateString() : "—"}</td>
              <td>
                <Button size="sm" variant="outline-primary" onClick={() => navigate(`/s/assessment/result/${a._id}`)}>
                  Review
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AssessmentInfoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [infoRes, attemptsRes] = await Promise.all([
        apiClient.get(`/student-assessments/${id}/info`),
        apiClient.get(`/student-assessments/${id}/my-attempts`),
      ]);
      setAssessment(infoRes.data.assessment);
      setAttempts(attemptsRes.data.attempts || []);
    } catch {
      setToast({ show: true, message: "Failed to load assessment", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const { data } = await apiClient.post(`/student-assessments/${id}/start`);
      navigate(`/s/assessment/take/${data.attempt._id}`);
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to start", type: "error" });
    } finally {
      setStarting(false);
      setConfirmStart(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>;
  }

  if (!assessment) {
    return <div className="text-center py-5 text-muted">Assessment not found</div>;
  }

  const a = assessment;
  const maxReached = a.maxAttempts > 0 && a.attemptsUsed >= a.maxAttempts;
  const now = new Date();
  const isExpired = a.endDate && new Date(a.endDate) < now;
  const notStartedYet = a.startDate && new Date(a.startDate) > now;
  const canStart = !maxReached && !isExpired && !notStartedYet;

  const getDisabledLabel = () => {
    if (isExpired) return "Expired";
    if (notStartedYet) return "Not Started Yet";
    return "Max Attempts Reached";
  };

  const renderStartButton = () => {
    if (a.inProgressAttemptId) {
      return (
        <Button variant="warning" onClick={() => navigate(`/s/assessment/take/${a.inProgressAttemptId}`)}>
          <Play size={16} className="me-1" /> Continue Assessment
        </Button>
      );
    }
    if (canStart) {
      return (
        <Button variant="primary" onClick={() => setConfirmStart(true)} disabled={starting}>
          {starting ? <Spinner animation="border" size="sm" className="me-1" /> : <Play size={16} className="me-1" />}
          Start Assessment
        </Button>
      );
    }
    return <Button variant="secondary" disabled>{getDisabledLabel()}</Button>;
  };

  const confirmMessage = a.timeLimit > 0
    ? `You are about to start "${a.title}". You will have ${a.timeLimit} minutes. Are you ready?`
    : `You are about to start "${a.title}". Are you ready?`;

  return (
    <div className="assessment-info-page">
      <Button variant="link" className="mb-3 p-0 text-decoration-none" onClick={() => navigate("/s/assessments")}>
        <ArrowLeft size={16} className="me-1" /> Back to Assessments
      </Button>

      <div className="info-card">
        <div className="info-card__title">{a.title}</div>
        {a.description && <div className="info-card__description">{a.description}</div>}

        <div className="info-card__details">
          <div className="info-card__detail">
            <HelpCircle size={16} /> {a.questionCount} questions
          </div>
          <div className="info-card__detail">
            <Clock size={16} /> {a.timeLimit > 0 ? `${a.timeLimit} min` : "No time limit"}
          </div>
          <div className="info-card__detail">
            <RefreshCw size={16} /> {a.attemptsUsed}/{a.maxAttempts > 0 ? a.maxAttempts : "∞"} attempts used
          </div>
          <div className="info-card__detail">
            <BarChart3 size={16} /> {a.passingPercentage > 0 ? `${a.passingPercentage}% to pass` : "No passing score"}
          </div>
          <div className="info-card__detail">
            <Shield size={16} /> Mode: {MODE_LABELS[a.mode]}
          </div>
          {a.negativeMarking && (
            <div className="info-card__detail text-danger">
              <XCircle size={16} /> Negative marking enabled
            </div>
          )}
        </div>

        {a.instructions && (
          <div className="info-card__instructions">{a.instructions}</div>
        )}

        <div className="info-card__actions">
          {renderStartButton()}
        </div>
      </div>

      <AttemptsTable attempts={attempts} navigate={navigate} />

      <ConfirmModal
        show={confirmStart}
        onHide={() => setConfirmStart(false)}
        onConfirm={handleStart}
        title="Start Assessment"
        message={confirmMessage}
        confirmText="Start Now"
        variant="primary"
      />

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default AssessmentInfoPage;
